// Per-scholars program accent color for the Scholars queue cards AND the applicant-details chip
// (both call subProgramColor, so any change here updates both surfaces together).
//
// Colors are an explicit per-scholars program map (SUBPROGRAM_COLORS) so they can be tuned at whim —
// edit a single entry to recolor that one program. Any subProgramId NOT in the map falls back to a
// deterministic hash color, so newly added programs still get a distinct color automatically without
// requiring an edit. The map is keyed by subProgramId (term-agnostic): a program keeps its color
// across terms/cycles.
//
// The hues below are deliberately spread around the wheel so visually-similar neighbors are pulled
// apart (the earlier hash-only scheme collided e.g. ART/HUM/BSC and SWS/GES/PSL/MTS).

const SATURATION = 75; // percent — vivid but not neon (fallback hash colors)
const LIGHTNESS = 55;  // percent — mid-tone so it works as a border + text color on white

// Explicit colors per known scholars program (edit freely). Full HSL so saturation/lightness can be
// tuned per program if a specific one needs to read darker/lighter. Keyed by subProgramId.
const SUBPROGRAM_COLORS = {
    ART: 'hsl(210, 72%, 50%)', // Arts — blue
    BSC: 'hsl(145, 53%, 37%)', // Biological Sciences — green
    BUS: 'hsl(304, 61%, 41%)', // Business — violet
    ENR: 'hsl(95, 61%, 52%)',  // Environment & Natural Resources — leaf green
    GES: 'hsl(25, 98%, 46%)',  // Engineering — orange
    HES: 'hsl(50, 64%, 43%)',  // Humanitarian Engineering — gold
    HSC: 'hsl(0, 98%, 34%)', // Health Sciences — magenta
    HUM: 'hsl(0, 72%, 52%)',   // Humanities — red
    IAF: 'hsl(187, 50%, 36%)', // International Affairs Studies — teal
    ICE: 'hsl(300, 77%, 59%)', // Entrepreneurship and Innovation — purple-pink
    MMC: 'hsl(219, 26%, 57%)', // Media, Marketing and Communication — indigo
    MTS: 'hsl(15, 55%, 40%)',  // Mount Leadership Society — brown/terracotta
    PSL: 'hsl(255, 45%, 45%)', // Law and Society — deep indigo
    STM: 'hsl(176, 69%, 46%)', // STEM Exploration and Engagement — deep teal-green
    SWS: 'hsl(53, 94%, 50%)', // Dunn Sport and Wellness — grass green
};

// Simple, stable string hash (djb2). Not cryptographic — just needs to spread ids across the hue
// wheel. Used only as the fallback for scholars programs not present in SUBPROGRAM_COLORS.
function hashString(str = '') {
    let hash = 5381;
    for (let i = 0; i < str.length; i += 1) {
        hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0; // hash * 33 + c, kept unsigned
    }
    return hash;
}

// Returns a CSS color (hsl) for a given scholars program id: the explicit mapped color when defined,
// otherwise a deterministic hash color. Empty/undefined ids fall back to a neutral hue so the card
// still renders sensibly.
export function subProgramColor(subProgramId) {
    if (!subProgramId) {
        return `hsl(0, 0%, ${LIGHTNESS}%)`;
    }
    if (SUBPROGRAM_COLORS[subProgramId]) {
        return SUBPROGRAM_COLORS[subProgramId];
    }
    const hue = hashString(subProgramId) % 360;
    return `hsl(${hue}, ${SATURATION}%, ${LIGHTNESS}%)`;
}
